/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * TwoBaseTensSubtraction.web.tsx - Shows a subtraction in base 10 with blocks and strikethroughs.
 */

import { hintImages } from '../HintImages';


interface TwoBaseTensSubtractionProps {
  /** The minuend. */
  numA: number;
  /** The subtrahend. */
  numB: number;
}

const TwoBaseTensSubtraction = ({ numA, numB }: TwoBaseTensSubtractionProps) => {
  const aTens = [];
  const aOnes = [];
  const bTens = [];
  const bOnes = [];

  const {
    numATens, numAOnes, numBTens, numBOnes
  } = calcBase(numA, numB);

  // Populate the frames
  for (let i = 0; i < numATens; i++) {
    const imageSource = 'yellow1';
    const key = imageSource + i.toString();
    aTens.push(
      <img className="base_ten_image" src={hintImages[imageSource].uri} key={key} />
    );
  }

  // Tens Right
  for (let i = 0; i < numBTens; i++) {
    const imageSource = 'yellow1sub';
    const key = imageSource + i.toString();
    bTens.push(
      <img className="base_ten_image" src={hintImages[imageSource].uri} key={key} />
    );
  }

  // Ones
  if (numAOnes !== '') {
    const key = numAOnes;
    aOnes.push(
      <img className="base_ten_image_ones" src={hintImages[numAOnes].uri} key={key} />
    );
  }

  if (numBOnes !== '') {
    const key = numAOnes;
    bOnes.push(
      <img className="base_ten_image_ones" src={hintImages[numBOnes].uri} key={key} />
    );
  }

  return (
    <div id="hint_base_ten">
      {aTens.length !== 0 && (
        <div className="base_ten_tens">
          {aTens}
        </div>
      )}
      {bTens.length !== 0 && (
        <div className="base_ten_tens">
          {bTens}
        </div>
      )}
      {aOnes.length !== 0 && (
        <div className="base_ten_ones">
          {aOnes}
        </div>
      )}

      {bOnes.length !== 0 && (
        <div className="base_ten_ones">
          {bOnes}
        </div>
      )}
    </div>
  );
};

export default TwoBaseTensSubtraction;

const calcBase = (numA: number, numB: number) => {
  const answer = numA - numB;

  let numATens; let numAOnes; let numBTens; let
    numBOnes;

  let numAOnesImageSource = '';
  let numBOnesImageSource = '';

  // Get the number of tens of the answer
  const tensAns = Math.floor(answer / 10);

  // Get the number of tens of the minuend
  const tensMin = Math.floor(numA / 10);

  // Check to see if numB is a whole ten
  if (numB > 9 && numB % 10 === 0) {
    numATens = Math.floor(answer / 10);
    numBTens = Math.floor(numB / 10);

    // Last digit of numA
    numAOnes = numA % 10;
    if (numAOnes !== 0) {
      numAOnesImageSource = `red${numAOnes.toString()}_0sub`;
    }
  }

  // Check to see if the sum crosses the ten boundary
  else if (tensAns < tensMin) {
    numATens = Math.floor(answer / 10);
    numBTens = Math.floor(numB / 10) - 1;

    // Last digit of numA
    numBOnes = numA % 10;
    if (numBOnes !== 0) {
      numBOnesImageSource = `red${numBOnes.toString()}_${numBOnes.toString()}sub`;
    }

    numAOnes = numB - numBOnes;
    numAOnesImageSource = 'red10' + `_${numAOnes.toString()}sub`;
  } else {
    numATens = Math.floor(answer / 10);
    numAOnes = answer - (numATens * 10);
    numBTens = Math.floor(numB / 10);
    numBOnes = numB - (numBTens * 10);

    let numOnes = numAOnes + numBOnes;

    if (numOnes === 0) {
      numOnes = 10;
    }
    numAOnesImageSource = `red${numOnes.toString()}_${numBOnes.toString()}sub`;
  }

  return {
    numATens, numAOnes: numAOnesImageSource, numBTens, numBOnes: numBOnesImageSource
  };
};
